import React, { useState, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, useLocation } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Navbar from "./components/Navbar";
import Dashboard from "./pages/Dashboard";
import Contact from "./pages/Contact";
import About from "./pages/About";
// import Login from "./pages/Login";
// import Signup from "./pages/Signup";
import VehicleRegistration from "./pages/VehicleRegistration";
import ServiceType from "./pages/Service-Type";
import ApplicationForm from "./pages/Application-Form";
import ServiceCategory from "./pages/Service-Category";
import TermsAndConditions from "./pages/terms-and-conditions";
import PrivacyPolicy from "./pages/privacy-policy";
import CancellationPolicy from "./pages/cancellation-policy";
import MyApplications from "./pages/MyApplications";
import UserDashboard from "./pages/UserDashboard";
import ApplicationDetailsUser from "./pages/ApplicationDetails";
import PaymentForm from "./pages/PaymentForm";
import PublicPayment from "./pages/PublicPayment";
import Pricing from "./pages/Pricing";
import Verify from "./pages/Verify";

// Import Admin Pages
import AdminLogin from "./admin/AdminLogin";
import AdminDashboard from "./admin/AdminDashboard";
import UserProfile from "./admin/UserProfile";
import FormFieldManagement from "./admin/FormFieldManagement";
import ServiceManagement from "./admin/ServiceManagement";
import ApplicationDetails from "./admin/ApplicationDetails";
import ApplicationsPage from "./admin/ApplicationsPage";
import UsersPage from "./admin/UsersPage";
import EmployeeManagement from "./admin/EmployeeManagement";
import PaymentsPage from "./admin/PaymentsPage";
import PaymentSettings from "./admin/PaymentSettings";
import ServiceStateAccessManagement from "./admin/ServiceStateAccessManagement";

// Import OTP-based Login and Signup Modals
import LoginModal from "./components/LoginModal_OTP";
import SignupModal from "./components/SignupModal_OTP";

// Import Removed: PasswordChangeModal (deprecated for OTP-only auth)
import PaymentModal from "./components/PaymentModal";
import { API_BASE_URL } from "./config/api";

function AnimatedRoutes({ toggleDarkMode, isDark, isLoginModalOpen, openLoginModal, closeLoginModal, isSignupModalOpen, openSignupModal, closeSignupModal, openPaymentModal }) {
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith("/admin");

  return (
    <>
      {!isAdminRoute && <Navbar toggleDarkMode={toggleDarkMode} isDark={isDark} openLoginModal={openLoginModal} openSignupModal={openSignupModal} />}
      <AnimatePresence mode="wait">
        <Routes location={location} key={location.pathname}>
          {/* User Routes */}
          <Route path="/" element={<Dashboard openLoginModal={openLoginModal} />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/about" element={<About />} />
          <Route path="/my-applications" element={<MyApplications openLoginModal={openLoginModal} />} />
          <Route path="/user-dashboard" element={<UserDashboard openLoginModal={openLoginModal} />} />
          {/* <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} /> */}
          <Route path="/vehicle-registration" element={<VehicleRegistration />} />
          <Route path="/service-type/:serviceName" element={<ServiceType />} />
          <Route path="/application-form/:id" element={<ApplicationForm />} />
          <Route path="/service-cat/:serviceName/:serviceType" element={<ServiceCategory />} />
          <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/cancellation-policy" element={<CancellationPolicy />} />
          <Route path="/application-details/:applicationId" element={<ApplicationDetailsUser />} />
          <Route path="/verify/:code" element={<Verify />} />
          <Route path="/payment-form" element={<PaymentForm openLoginModal={openLoginModal} />} />
          <Route path="/public-payment/:token" element={<PublicPayment />} />
          <Route path="/pricing" element={<Pricing />} />

          {/* Admin Routes */}
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin/login/:role" element={<AdminLogin />} />
          <Route path="/admin/dashboard" element={<AdminDashboard />} />
          <Route path="/admin/applications" element={<ApplicationsPage />} />
          <Route path="/admin/users" element={<UsersPage />} />
          <Route path="/admin/employees" element={<EmployeeManagement />} />
          <Route path="/admin/payments" element={<PaymentsPage />} />
          <Route path="/admin/payment-settings" element={<PaymentSettings />} />
          <Route path="/admin/user/:userId" element={<UserProfile />} />
          <Route path="/admin/application/:applicationId" element={<ApplicationDetails />} />
          <Route path="/admin/services" element={<ServiceManagement />} />
          <Route path="/admin/form-fields" element={<FormFieldManagement />} />
          <Route path="/admin/service-state-access" element={<ServiceStateAccessManagement />} />
        </Routes>
      </AnimatePresence>
    </>
  );
}

export default function App() {
  const [isDark, setIsDark] = useState(false);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [isSignupModalOpen, setIsSignupModalOpen] = useState(false);

  // Global Modal States
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [paymentUserId, setPaymentUserId] = useState(null);
  const [verifiedApplications, setVerifiedApplications] = useState([]);

  // keep theme in localStorage
  useEffect(() => {
    const savedTheme = localStorage.getItem("theme");
    if (savedTheme === "dark") setIsDark(true);
    else setIsDark(false); // Default to light if no saved theme or if saved theme is light
  }, []);

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", isDark ? "dark" : "light");
    localStorage.setItem("theme", isDark ? "dark" : "light");
  }, [isDark]);

  // Automated Payment Requirement Check
  useEffect(() => {
    const checkPaymentRequired = async (userId) => {
      if (!userId) return;
      try {
        const response = await fetch(`${API_BASE_URL}/applications/user/${userId}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });

        if (response.ok) {
          const data = await response.json();
          const applications = Array.isArray(data) ? data : (data.applications || []);
          const verifiedApps = applications.filter(app =>
            ['documents verified', 'payment pending'].includes((app.ApplicationStatus || '').toLowerCase())
          );

          if (verifiedApps.length > 0) {
            setPaymentUserId(userId);
            setVerifiedApplications(verifiedApps);
            setIsPaymentModalOpen(true);
          }
        }
      } catch (err) {
        console.error('Error checking payment requirements:', err);
      }
    };

    const handleInitialCheck = () => {
      // Skip check if on admin route or admin is logged in
      if (window.location.pathname.startsWith('/admin') || localStorage.getItem('adminAuth') === 'true') {
        return;
      }

      const userStr = localStorage.getItem('user');
      if (userStr) {
        try {
          const user = JSON.parse(userStr);
          if (user.userId) {
            checkPaymentRequired(user.userId);
          }
        } catch (e) {
          console.error("Error parsing user for payment check", e);
        }
      }
    };

    // Check once on mount
    handleInitialCheck();

    // Re-check on storage changes (like login from another tab, though we dispatch manually too)
    const handleStorage = () => {
      handleInitialCheck();
    };

    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  const toggleDarkMode = () => setIsDark(!isDark);

  const openLoginModal = () => setIsLoginModalOpen(true);
  const closeLoginModal = () => setIsLoginModalOpen(false);

  const openSignupModal = () => setIsSignupModalOpen(true);
  const closeSignupModal = () => setIsSignupModalOpen(false);

  const openPaymentModal = (userId, apps) => {
    setPaymentUserId(userId);
    setVerifiedApplications(apps);
    setIsPaymentModalOpen(true);
  };

  const closePaymentModal = () => {
    setIsPaymentModalOpen(false);
    setPaymentUserId(null);
    setVerifiedApplications([]);
  };

  const handlePaymentSuccess = () => {
    closePaymentModal();
    // Refresh navbar or other components
    window.dispatchEvent(new Event("storage"));
    window.dispatchEvent(new Event("payment-submitted"));
    alert("Payment information submitted successfully!");
  };



  return (
    <Router>
      <AnimatedRoutes
        toggleDarkMode={toggleDarkMode}
        isDark={isDark}
        isLoginModalOpen={isLoginModalOpen}
        openLoginModal={openLoginModal}
        closeLoginModal={closeLoginModal}
        isSignupModalOpen={isSignupModalOpen}
        openSignupModal={openSignupModal}
        closeSignupModal={closeSignupModal}
        openPaymentModal={openPaymentModal}
      />
      <LoginModal
        isOpen={isLoginModalOpen}
        onClose={closeLoginModal}
        onLoginSuccess={closeLoginModal}
        openSignupModal={openSignupModal}
        isDark={isDark}
        openPaymentModal={openPaymentModal}
      />
      <SignupModal
        isOpen={isSignupModalOpen}
        onClose={closeSignupModal}
        onSignupSuccess={closeSignupModal}
        openLoginModal={openLoginModal}
        isDark={isDark}
      />

      {/* Global Modals */}
      <PaymentModal
        isOpen={isPaymentModalOpen && !window.location.pathname.startsWith('/admin') && localStorage.getItem('adminAuth') !== 'true'}
        userId={paymentUserId}
        verifiedApplications={verifiedApplications}
        onPaymentSuccess={handlePaymentSuccess}
        onClose={closePaymentModal}
        isDark={isDark}
      />

      <ToastContainer />
    </Router>
  );
}
